import { z } from 'zod';

export const API_TOKEN_PREFIX = 'dth_';

export const apiTokenScopeSchema = z.enum(['webhook:write', 'resource:write', 'resource:read']);
export type ApiTokenScope = z.infer<typeof apiTokenScopeSchema>;

/**
 * v2 (canonical) — what we write today. The plaintext is never stored; we keep
 * a SHA-256 hash plus a 4-char preview that's safe to render in the UI.
 */
export const apiTokenSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(80),
  tokenHash: z.string(),
  tokenPreview: z.string(),
  scopes: z.array(apiTokenScopeSchema).default([]),
  isActive: z.boolean().default(true),
  lastUsedAt: z.string().datetime().nullable().default(null),
  expiresAt: z.string().datetime().nullable().default(null),
  createdAt: z.string().datetime(),
  createdBy: z.string().email(),
});
export type ApiToken = z.infer<typeof apiTokenSchema>;

export const createApiTokenSchema = z.object({
  name: z.string().min(1).max(80),
  scopes: z.array(apiTokenScopeSchema).min(1),
  expiresInDays: z.number().int().positive().max(365).optional(),
});
export type CreateApiTokenInput = z.infer<typeof createApiTokenSchema>;

export const createApiTokenResponseSchema = apiTokenSchema.extend({
  plaintextToken: z.string().describe('Returned ONLY on creation. Never stored.'),
});
export type CreateApiTokenResponse = z.infer<typeof createApiTokenResponseSchema>;

/**
 * v1 (legacy) — the GAS frontend wrote tokens directly to Firestore in plain
 * text under collection `api_tokens` (snake_case). It also used `permissions`
 * instead of `scopes` and stored `createdAt` / `expiresAt` as numeric strings
 * (millis) instead of ISO datetimes. See `GAS (legacy)/index.html:4134-4142`.
 *
 * We keep this as a separate read-only schema so the new admin UI can list
 * those legacy tokens with a "v1 legacy" badge instead of silently dropping
 * them. Verification of incoming webhooks falls back to v1 if no v2 match
 * exists. Rotating to v2 is a manual admin action.
 */
const v1Permission = z.union([
  apiTokenScopeSchema,
  z
    .enum(['read', 'write'])
    .transform((p): ApiTokenScope => (p === 'write' ? 'resource:write' : 'resource:read')),
]);

const epochOrIsoString = z.union([
  z.string().datetime(),
  z.string().transform((s, ctx) => {
    // Legacy GAS stored Date.now() millis as a string (e.g. "1704123456789").
    const ms = Number(s);
    if (!Number.isFinite(ms) || ms <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid epoch string' });
      return z.NEVER;
    }
    return new Date(ms).toISOString();
  }),
]);

export const apiTokenLegacySchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  /** Plaintext value as stored by GAS. Never re-exposed in API responses. */
  token: z.string(),
  permissions: z.array(v1Permission).default([]),
  isActive: z.boolean().default(true),
  expiresAt: epochOrIsoString.nullable().default(null),
  createdAt: epochOrIsoString,
  createdBy: z.string().default(''),
});
export type ApiTokenLegacy = z.infer<typeof apiTokenLegacySchema>;

/**
 * Discriminated shape returned by `GET /tokens`. v2 items lose their
 * `tokenHash` before serialization; v1 items lose their plaintext `token`
 * and only expose a short `tokenPreview`. Frontend renders a "Legacy" badge
 * on `version: 'v1'` rows and hides the "rotate to v2" CTA otherwise.
 */
export type ApiTokenListItem =
  | ({ version: 'v2' } & Omit<ApiToken, 'tokenHash'>)
  | {
      version: 'v1';
      id: string;
      name: string;
      tokenPreview: string;
      scopes: ApiToken['scopes'];
      isActive: boolean;
      lastUsedAt: null;
      expiresAt: string | null;
      createdAt: string;
      createdBy: string;
    };
